import React, { useState } from "react";

import { Pagination, PrimaryButton } from "widgets";
import CreateSchoolDialog from "components/CreateSchoolDialog/CreateSchoolDialog";
import SearchSchools from "components/SearchSchools/SearchSchools";
import SchoolsList from "components/SchoolsList/SchoolsList";
import { useSearchSchools } from "store/backend/apiHooks";
import Loader from "shared/Icons/Loader/Loader";
import {
  DEFAULT_SCHOOL_PAGE_NUMBER,
  SEARCH_SCHOOL_BY_SCHOOL_CODE,
  SEARCH_SCHOOL_BY_UDISE_CODE,
  SCHOOL_API_RESPONSE_FIELDS,
  SCHOOL_PAGE_SIZE_OPTIONS,
} from "constants/schoolConstants";
import {
  canCreateSchool,
  getSchoolApiErrorMessage,
  isSchoolSearchEnabled,
} from "helpers/schoolUtils";

import styles from "./Schools.module.scss";

const Schools = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchSchoolBy, setSearchSchoolBy] = useState(
    SEARCH_SCHOOL_BY_UDISE_CODE,
  );
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(DEFAULT_SCHOOL_PAGE_NUMBER);
  const [limit, setLimit] = useState(SCHOOL_PAGE_SIZE_OPTIONS[0]);
  const [hasSearched, setHasSearched] = useState(false);
  const {
    mutate: searchSchools,
    data: searchResponse,
    isPending: isLoading,
    isError,
    error,
    reset,
  } = useSearchSchools();
  const schools = searchResponse?.data?.[SCHOOL_API_RESPONSE_FIELDS.SCHOOLS];
  const hasSchoolPayload = Array.isArray(schools);
  const totalCount =
    searchResponse?.data?.[SCHOOL_API_RESPONSE_FIELDS.TOTAL_COUNT];
  const totalPages =
    searchResponse?.data?.[SCHOOL_API_RESPONSE_FIELDS.TOTAL_PAGES] ??
    DEFAULT_SCHOOL_PAGE_NUMBER;
  const currentPage =
    searchResponse?.data?.[SCHOOL_API_RESPONSE_FIELDS.PAGE] ?? page;
  const pageSize =
    searchResponse?.data?.[SCHOOL_API_RESPONSE_FIELDS.PAGE_SIZE] ?? limit;
  const showPagination =
    hasSearched && hasSchoolPayload && !isLoading && !isError;
  const executeSearch = (targetPage, targetLimit) => {
    const trimmedSearchValue = searchValue.trim();
    if (!isSchoolSearchEnabled(searchSchoolBy, searchValue)) {
      return;
    }
    const payload = {
      page: targetPage,
      pageSize: targetLimit,
    };
    if (searchSchoolBy === SEARCH_SCHOOL_BY_UDISE_CODE) {
      payload.udiseCode = trimmedSearchValue;
    } else if (searchSchoolBy === SEARCH_SCHOOL_BY_SCHOOL_CODE) {
      payload.schoolCode = trimmedSearchValue.toUpperCase();
    } else {
      payload.schoolName = trimmedSearchValue;
    }
    searchSchools(payload);
  };
  const handleSearchClick = () => {
    setPage(DEFAULT_SCHOOL_PAGE_NUMBER);
    setHasSearched(true);
    executeSearch(DEFAULT_SCHOOL_PAGE_NUMBER, limit);
  };
  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    executeSearch(nextPage, limit);
  };
  const handlePageSizeChange = (nextLimit) => {
    setLimit(nextLimit);
    setPage(DEFAULT_SCHOOL_PAGE_NUMBER);
    executeSearch(DEFAULT_SCHOOL_PAGE_NUMBER, nextLimit);
  };
  const handleClear = () => {
    setSearchValue("");
    setPage(DEFAULT_SCHOOL_PAGE_NUMBER);
    setHasSearched(false);
    reset();
  };
  return (
    <div className={styles.mainContainer}>
      <div className={styles.page}>
        <header className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Schools</h1>
          {canCreateSchool() ? (
            <div className={styles.pageActions}>
              <PrimaryButton onClick={() => setCreateDialogOpen(true)}>
                Create School
              </PrimaryButton>
            </div>
          ) : null}
        </header>
        <div className={styles.searchContainer}>
          <h2 className={styles.searchSectionTitle}>Search School</h2>
          <SearchSchools
            searchSchoolBy={searchSchoolBy}
            setSearchSchoolBy={setSearchSchoolBy}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            handleOnClickSearchButton={handleSearchClick}
            isLoading={isLoading}
            handleOnClickClearButton={handleClear}
          />
        </div>
        <section
          className={styles.resultsPanel}
          aria-labelledby="schools-search-results-heading"
        >
          <header className={styles.resultsPanelHeader}>
            <div className={styles.resultsTitleRow}>
              <h2
                id="schools-search-results-heading"
                className={styles.resultsHeading}
              >
                Search Results
              </h2>
              {isLoading ? (
                <span className={styles.resultsHeaderMeta}>Searching…</span>
              ) : hasSearched && typeof totalCount === "number" ? (
                <span className={styles.resultsCountBadge} aria-live="polite">
                  {totalCount === 1 ? "1 school" : `${totalCount} schools`}
                </span>
              ) : null}
            </div>
          </header>
          <div className={styles.resultsBody}>
            {isLoading ? (
              <div className={styles.resultsStateWrap}>
                <Loader />
              </div>
            ) : isError ? (
              <div className={styles.errorMessage}>
                {getSchoolApiErrorMessage(error)}
              </div>
            ) : !searchValue.trim() ? (
              <p className={styles.resultsEmpty}>
                Enter a search term above, then choose <strong>Search</strong>{" "}
                to load schools here.
              </p>
            ) : schools?.length > 0 ? (
              <SchoolsList schools={schools} />
            ) : hasSearched && schools?.length === 0 ? (
              <p className={styles.resultsEmpty}>
                No schools matched your criteria. Try another UDISE code,
                school code or name or clear and search again.
              </p>
            ) : searchValue.trim() && !hasSearched ? (
              <p className={styles.resultsEmpty}>
                You have a search term ready. Click <strong>Search</strong> to
                view matching schools.
              </p>
            ) : null}
          </div>
          {showPagination && (
            <footer className={styles.resultsFooter}>
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                totalItems={totalCount}
                pageSize={pageSize}
                pageSizeOptions={SCHOOL_PAGE_SIZE_OPTIONS}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                disabled={isLoading}
              />
            </footer>
          )}
        </section>
      </div>
      <CreateSchoolDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </div>
  );
};

export default Schools;
